(function () {
  const containerSelector = "[data-bb-yotpo-featured-review]";
  const reviewSelector = ".yotpo-reviews-item, [id^='yotpo-review-item-']";
  const maxAttempts = 24;
  const retryDelay = 500;

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

  const getText = (root, selectors) => {
    for (const selector of selectors) {
      const element = root.querySelector(selector);
      const text = element && element.textContent ? element.textContent.trim().replace(/\s+/g, " ") : "";
      if (text) return text;
    }
    return "";
  };

  const getRating = (root) => {
    const ratingElement =
      root.querySelector(".yotpo-review-star-rating[aria-label]") ||
      root.querySelector("[aria-label*='out of 5 stars']");
    const label = ratingElement ? ratingElement.getAttribute("aria-label") || "" : "";
    const match = label.match(/([0-5](?:\.\d+)?)\s+out of 5 stars/i);
    return match ? Math.round(Number(match[1])) : 0;
  };

  const getReviewId = (review) => {
    const rawId = review.id || review.dataset.reviewId || review.getAttribute("data-review-id") || "";
    return rawId.replace(/^yotpo-review-item-/, "").trim();
  };

  const parseReview = (review) => {
    const author = getText(review, [".yotpo-reviewer-name-text", ".yotpo-reviewer-name"]);
    const body = getText(review, [".yotpo-read-more-text", ".yotpo-review-content"]);
    const date = getText(review, [".yotpo-date-format"]);
    const verified = getText(review, [".yotpo-reviewer-verified-buyer-text"]);
    const rating = getRating(review);

    if (!author || body.length < 24 || rating <= 0) return null;
    return { id: getReviewId(review), author, body, date, rating, verified: /verified buyer/i.test(verified) };
  };

  const findReview = (preferredId) => {
    const reviews = Array.from(document.querySelectorAll(reviewSelector));
    const parsedReviews = reviews.map(parseReview).filter(Boolean);
    if (preferredId) {
      const preferred = parsedReviews.find((review) => review.id === preferredId);
      if (preferred) return preferred;
    }
    return parsedReviews[0] || null;
  };

  const getYotpoAppKey = () => {
    const loader = Array.from(document.scripts).find((script) => /yotpo\.com\/v1\/loader\//.test(script.src));
    const match = loader?.src.match(/\/loader\/([^/?]+)/);
    return match ? match[1] : "";
  };

  const formatYotpoDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit"
    }).format(date);
  };

  const fetchConfiguredReview = async (container) => {
    const reviewId = container.dataset.bbFeaturedReviewId;
    const productId = container.dataset.bbYotpoProductId;
    const appKey = getYotpoAppKey();
    if (!reviewId || !productId || !appKey) return null;

    try {
      const endpoint = `https://api-cdn.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/${encodeURIComponent(productId)}/reviews.json?per_page=100`;
      let page = 1;
      let totalPages = 1;
      let review = null;

      do {
        const response = await fetch(`${endpoint}&page=${page}`, { credentials: "omit" });
        if (!response.ok) return null;
        const payload = await response.json();
        review = payload?.response?.reviews?.find((item) => String(item.id) === String(reviewId));
        totalPages = Math.max(Number(payload?.response?.pagination?.total_pages) || 1, 1);
        page += 1;
      } while (!review && page <= totalPages);

      if (!review || !review.user?.display_name || !review.content || !review.score) return null;
      return {
        id: String(review.id),
        author: review.user.display_name,
        body: review.content,
        date: formatYotpoDate(review.created_at),
        rating: Math.round(Number(review.score)),
        verified: review.verified_buyer === true
      };
    } catch (_) {
      return null;
    }
  };

  const renderStars = (rating) =>
    Array.from({ length: Math.min(Math.max(rating, 1), 5) }, () =>
      '<svg aria-hidden="true" viewBox="0 0 18 18" focusable="false"><path d="M9 14.118 14.562 17.475 13.086 11.148 18 6.891 11.529 6.342 9 .375 6.471 6.342 0 6.891 4.914 11.148 3.438 17.475 9 14.118Z"/></svg>'
    ).join("");

  const renderReview = (container, review) => {
    const verifiedHtml = review.verified
      ? '<span class="bb-product-detail__featured-review-verified"><svg aria-hidden="true" viewBox="0 0 12 12" focusable="false"><path d="M10.4 3.1 4.9 8.6 2.2 5.9" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>Verified Buyer</span>'
      : "";
    const attribution = review.date ? `${review.author}, ${review.date}` : review.author;

    container.innerHTML = `
      <div class="bb-product-detail__featured-review-head">
        <span class="bb-product-detail__featured-review-stars" aria-label="${review.rating} out of 5 stars">${renderStars(review.rating)}</span>
        ${verifiedHtml}
      </div>
      <p class="bb-product-detail__featured-review-body">&ldquo;${escapeHtml(review.body)}&rdquo;</p>
      <p class="bb-product-detail__featured-review-attribution">&mdash; ${escapeHtml(attribution)}</p>
    `;
    const isConfiguredReview = Boolean(container.dataset.bbFeaturedReviewId) &&
      review.id === container.dataset.bbFeaturedReviewId;
    container.dataset.bbYotpoSource = isConfiguredReview
      ? "configured-yotpo-review"
      : "first-complete-yotpo-review";
    if (review.id) container.dataset.bbYotpoReviewId = review.id;
    container.hidden = false;
  };

  const init = () => {
    const container = document.querySelector(containerSelector);
    if (!container) return;

    let attempts = 0;
    const configuredReviewPromise = fetchConfiguredReview(container);
    const tryRender = async () => {
      if (!document.body.contains(container)) return;
      const configuredReview = await configuredReviewPromise;
      const review = configuredReview || findReview(container.dataset.bbFeaturedReviewId || "");
      if (review) {
        renderReview(container, review);
        return;
      }

      attempts += 1;
      if (attempts < maxAttempts) {
        window.setTimeout(tryRender, retryDelay);
      }
    };

    tryRender();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();
